import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Twitter, Mail, Instagram, Facebook } from 'lucide-react';

const footerLinks = {
  company: [
    { name: 'nav.about', href: '/about' },
    // { name: 'nav.investors', href: '/investors' }, // HIDDEN UNTIL LAUNCH
  ],
  legal: [
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Cookie Policy', href: '/cookies' },
  ],
};

const socialLinks = [
  { name: 'Twitter',   icon: Twitter,   isSocial: true },
  { name: 'Instagram', icon: Instagram, isSocial: true },
  { name: 'Facebook',  icon: Facebook,  isSocial: true },
  { name: 'Email',     icon: Mail,      isSocial: false, href: 'mailto:contact@5n6.co.uk' },
];

function SocialLinks() {
  const [dimmed, setDimmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleSocialClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setDimmed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDimmed(false), 6000);
  };

  return (
    <div>
      <div className="flex gap-4">
        {socialLinks.map((item) =>
          item.isSocial ? (
            <button
              key={item.name}
              type="button"
              onClick={handleSocialClick}
              aria-label={item.name}
              className="transition-all duration-500"
              style={{ color: dimmed ? 'rgba(122,122,122,0.3)' : '#7A7A7A' }}
            >
              <item.icon size={20} />
            </button>
          ) : (
            <a
              key={item.name}
              href={item.href}
              aria-label={item.name}
              className="transition-colors hover:text-ink"
              style={{ color: '#7A7A7A' }}
            >
              <item.icon size={20} />
            </a>
          )
        )}
      </div>

      <div
        className="overflow-hidden transition-all duration-500"
        style={{ maxHeight: dimmed ? '120px' : '0px', opacity: dimmed ? 1 : 0 }}
      >
        <p className="mt-3 text-xs text-ink-muted leading-relaxed" style={{ maxWidth: '280px' }}>
          We are a software company without an official social media presence, by choice. Contact us directly by email. We are always open to discussing new ideas.
        </p>
      </div>
    </div>
  );
}

function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setError('Failed to send. Please try again.');
      }
    } catch {
      setError('Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div>
        <h5 className="text-xs font-medium text-ink uppercase tracking-wider mb-4">
          Get in Touch
        </h5>
        <p className="text-sm text-ink-light">
          Message sent. We'll be in touch.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-xs font-medium text-ink uppercase tracking-wider mb-4">
        Get in Touch
      </h5>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          className="w-full bg-transparent border-b border-ink/20 text-ink text-xs py-2 placeholder-ink-muted/60 focus:outline-none focus:border-ink transition-colors"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          className="w-full bg-transparent border-b border-ink/20 text-ink text-xs py-2 placeholder-ink-muted/60 focus:outline-none focus:border-ink transition-colors"
        />
        <textarea
          placeholder="Message"
          minLength={10}
          value={message}
          onChange={e => setMessage(e.target.value)}
          required
          rows={3}
          className="w-full bg-transparent border-b border-ink/20 text-ink text-xs py-2 placeholder-ink-muted/60 focus:outline-none focus:border-ink transition-colors resize-none"
        />
        {error && (
          <p className="text-xs" style={{ color: '#9B3B3B' }}>{error}</p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full text-xs font-medium text-ink border border-ink/30 py-2 hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send Message'}
        </button>
      </form>
    </div>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [easterEgg, setEasterEgg] = useState(false);
  const [easterEggVisible, setEasterEggVisible] = useState(false);
  const eggTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eggFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (eggTimerRef.current) clearTimeout(eggTimerRef.current);
    if (eggFadeRef.current) clearTimeout(eggFadeRef.current);
  }, []);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (easterEgg) return;
    setEasterEgg(true);
    // Small delay so the transition feels intentional
    eggFadeRef.current = setTimeout(() => setEasterEggVisible(true), 30);
    eggTimerRef.current = setTimeout(() => {
      setEasterEggVisible(false);
      setTimeout(() => setEasterEgg(false), 700);
    }, 5000);
  };

  return (
    <footer id="contact" className="border-t border-paper-darker" style={{ backgroundColor: '#E0DDD8' }}>
      {/* Easter egg overlay */}
      <div
        style={{
          position: easterEgg ? 'relative' : 'absolute',
          height: easterEgg ? 'auto' : 0,
          overflow: 'hidden',
          opacity: easterEggVisible ? 1 : 0,
          transition: 'opacity 0.6s ease',
          pointerEvents: easterEgg ? 'auto' : 'none',
        }}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          style={{ padding: '80px 24px' }}
        >
          <span className="font-logo tracking-tight text-ink" style={{ fontSize: '120px', lineHeight: 1 }}>
            5N6
          </span>
          <p className="mt-6 text-xs text-ink-muted uppercase tracking-widest">
            Bedfordshire, England
          </p>
          <p className="mt-8 text-base text-ink-light" style={{ maxWidth: '400px', lineHeight: 1.7 }}>
            Good engineers notice things that were not meant to be found.
            <br />
            <span className="text-ink font-medium">You should get in touch.</span>
          </p>
          <a
            href="mailto:contact@5n6.co.uk"
            className="mt-8 text-xs font-medium text-ink border border-ink/30 px-6 py-2.5 hover:bg-ink hover:text-paper transition-colors"
          >
            contact@5n6.co.uk
          </a>
        </div>
      </div>

      {/* Normal footer content */}
      <div
        style={{
          height: easterEgg ? 0 : 'auto',
          overflow: 'hidden',
          opacity: easterEggVisible ? 0 : 1,
          transition: 'opacity 0.6s ease',
        }}
      >
        <div className="container-wide py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-8 lg:gap-12">
            {/* Brand */}
            <div style={{ marginTop: '-18px' }}>
              <button type="button" onClick={handleLogoClick} className="inline-block text-left focus:outline-none">
                <div className="flex flex-col">
                  <div className="relative leading-none" style={{ width: 'fit-content' }}>
                    <span className="font-logo tracking-tight text-ink text-[45px]">
                      5N6
                    </span>
                    <span
                      className="absolute tracking-widest uppercase font-semibold text-ink-muted"
                      style={{ fontSize: '12px', top: '0px', right: '-20px' }}
                    >
                      TM
                    </span>
                  </div>
                  <span className="text-xs text-ink-muted tracking-widest uppercase leading-tight" style={{ marginTop: '2px' }}>
                    Expertise X<br />Computation
                  </span>
                </div>
              </button>
              <p className="mt-2 text-sm text-ink-light max-w-xs">
                {t('footer.tagline')}
              </p>
              <div className="mt-3">
                <SocialLinks />
              </div>
            </div>

            {/* Right columns */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-10">
              {/* Company Links */}
              <div>
                <h5 className="text-xs font-medium text-ink uppercase tracking-wider">
                  {t('footer.company')}
                </h5>
                <ul className="mt-4 space-y-3">
                  {footerLinks.company.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="text-sm text-ink-light hover:text-ink transition-colors"
                      >
                        {t(item.name)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h5 className="text-xs font-medium text-ink uppercase tracking-wider">
                  {t('footer.legal')}
                </h5>
                <ul className="mt-4 space-y-3">
                  {footerLinks.legal.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="text-sm text-ink-light hover:text-ink transition-colors"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company Info */}
              <div>
                <h5 className="text-xs font-medium text-ink uppercase tracking-wider">
                  5N6 LTD
                </h5>
                <address className="mt-4 text-sm text-ink-light not-italic space-y-1">
                  <p>5 Tivoli House</p>
                  <p>Wigmore Park Centre</p>
                  <p>Luton, Bedfordshire</p>
                  <p>LU2 9DU</p>
                  <p>United Kingdom</p>
                </address>
              </div>

              {/* Contact Form */}
              <div>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ backgroundColor: '#2a2a2a' }}>
        <div className="container-wide py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm" style={{ color: '#e0e0e0' }}>
              &copy; {currentYear} {t('footer.copyright')}
            </p>
            <p className="text-sm" style={{ color: '#e0e0e0' }}>
              5N6™ is a{' '}
              <a
                href="https://trademarks.ipo.gov.uk/ipo-tmcase/page/Results/1/UK00004090540"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors underline"
              >
                registered trademark
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
